import {ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewEncapsulation} from '@angular/core';
import {FieldMap, QueryBuilderConfigType, Rule, RuleSet} from "../../_typing/query-builder";
import {QueryBuilderFormService} from "../../services/query-builder-form.service";
import {ComponentTypeFieldOptionService} from "./services/component-type-field-option.service";
import {EventTypeFieldOptionService} from "./services/event-type-field-option.service";
import {Observable} from "rxjs";
import {COMPONENT_EVENTS_CONFIG, isComponentEventsConfig} from "../../_typing/config/component-events-table";
import {takeUntil} from "rxjs/operators";
import {ModelID} from "../../_typing/generic-types";
import {isCustomEventsConfig} from "../../_typing/config/custom-events-table";

import {WireRuleSetComponent} from "./rule-set/wire-rule-set.component";
import {BaseComponent} from "../../shared/base.component";
import {QueryFieldSearchService} from "./services/query-field-search.service";
import {getDirectConstantPropertyIds} from "./query-field.utils";

@Component({
    selector: 'wire-query-builder',
    templateUrl: './wire-query-builder.component.html',
    styleUrls: ['./wire-query-builder.component.scss'],
    imports: [
        WireRuleSetComponent
    ],
    providers: [
        QueryBuilderFormService,
        ComponentTypeFieldOptionService,
        EventTypeFieldOptionService,
        QueryFieldSearchService
    ],
    encapsulation: ViewEncapsulation.None
})

export class WireQueryBuilderComponent extends BaseComponent {
    @Input() modelTypeId: ModelID;
    @Output() queryChange: EventEmitter<RuleSet> = new EventEmitter<RuleSet>();

    @Input() set query(value: RuleSet) {
        this._query = value || {...this.defaultQuery};
    }

    get query(): RuleSet {
        return this._query;
    }

    @Input() set config(config: QueryBuilderConfigType) {
        this._config = config;
    };

    @Input() set additionalFields(fields: FieldMap) {
        this._additionalFields = fields || {};
        this._mergeFields();
    }

    private _config: QueryBuilderConfigType;
    get config(): QueryBuilderConfigType {
        return this._config;
    }

    private readonly defaultQuery: RuleSet = {
        condition: 'and',
        rules: []
    };

    private _query: RuleSet = {...this.defaultQuery};
    fields: FieldMap;
    private _baseFields: FieldMap;
    private _additionalFields: FieldMap = {};

    constructor(private queryBuilderFormService: QueryBuilderFormService,
                private ctFieldOptionService: ComponentTypeFieldOptionService,
                private etFieldOptionService: EventTypeFieldOptionService,
                private queryFieldSearch: QueryFieldSearchService,
                private cdr: ChangeDetectorRef) {
        super();
    }

    override ngOnInit() {
        super.ngOnInit();
        if (this.config) {
            this.loadBuilder();
        }
    }

    loadBuilder() {
        let $fieldMap: Observable<FieldMap>;
        const queryPropertyIds = getDirectConstantPropertyIds(this.query);
        if (isComponentEventsConfig(this.config)) {
            const componentTypeId = (this.config as COMPONENT_EVENTS_CONFIG).selected_component_type?.id;
            this.queryFieldSearch.configure('component_types', componentTypeId);
            $fieldMap = this.ctFieldOptionService.loadComponentType(
                this.config as COMPONENT_EVENTS_CONFIG,
                true,
                queryPropertyIds
            );
        } else if (isCustomEventsConfig(this.config) && this.modelTypeId) {
            this.queryFieldSearch.configure('event_types', this.modelTypeId);
            $fieldMap = this.etFieldOptionService.loadEventType(
                this.config,
                this.modelTypeId,
                true,
                queryPropertyIds
            );
        } else if (this.config && 'fields' in this.config) {
            this.queryFieldSearch.disable();
            this._baseFields = this.config.fields;
            this._mergeFields();
            this._emitDefaultRule(this.config.fields);
            this.cdr.markForCheck();
            return;
        }

        if ($fieldMap) {
            $fieldMap.pipe(takeUntil(this.onDestroy)).subscribe(fields => {
                this._baseFields = {...fields};
                Object.keys(this._baseFields).map(key => this.addIdtoField(key, this._baseFields[key]));
                this._mergeFields();
            });
        }
    }

    ruleSetChanged(event: RuleSet) {
        this.queryChange.next(event);
    }

    private _mergeFields(): void {
        if (this._baseFields) {
            this.fields = {...this._baseFields, ...this._additionalFields};
            this.queryFieldSearch.setFields(this.fields);
        }
    }

    private _emitDefaultRule(fields: FieldMap): void {
        const firstKey = Object.keys(fields)[0];
        if (firstKey) {
            const field = fields[firstKey];
            const ops = this.queryBuilderFormService.operatorMap[field.type];
            this.queryBuilderFormService.defaultRuleChanged.next(
                new Rule(field.value || firstKey, ops?.[0])
            );
        }
    }

    private addIdtoField(key, value) {
        return Object.assign(value, {value: key});
    }

}
